import React, { useState } from 'react'
import { Button } from 'antd'
import { buildCsvString } from 'src/utils/csv'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'

const formatMoney = pennies => {
  const amount = Number(pennies || 0) / 100
  return `£${amount.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const fileName = (suppliers, start, end) => {
  const supplierPart =
    suppliers.length === 1 ? suppliers[0] : 'multiple-suppliers'
  return `spending-report_${supplierPart}_${start}_to_${end}.csv`
}

const buildRows = (tableRecords, venueReportsBySupplier) => {
  const header = [
    'Supplier',
    'Total Orders',
    'Total Venues',
    'Total Spend',
    'Venue',
    'Second Title',
    'Orders',
    'Products',
    'Venue Total Spend',
  ]

  const rows = [header]

  tableRecords.forEach(supplierRow => {
    const supplierName = supplierRow._id?.supplier || ''
    const totalOrders = supplierRow.orders || 0
    const totalVenues = supplierRow.venueIds || 0
    const totalSpend = formatMoney(supplierRow.totalSpendWithoutVat)

    const venues = venueReportsBySupplier[supplierName] || []
    if (venues.length === 0) {
      rows.push([
        supplierName,
        totalOrders,
        totalVenues,
        totalSpend,
        '',
        '',
        '',
        '',
        '',
      ])
    } else {
      venues.forEach(v => {
        rows.push([
          supplierName,
          totalOrders,
          totalVenues,
          totalSpend,
          v._id?.venue || '',
          v._id?.supplierSecondTitle || '',
          v.orders || 0,
          v.totalProducts || 0,
          formatMoney(v.totalSpendWithoutVat),
        ])
      })
    }
  })

  return rows
}

const fetchVenueBreakdown = async (startDate, endDate, supplier) => {
  const session = await Auth.currentAuthenticatedUser()
  const url = `supplier/spending?from=${startDate}&to=${endDate}&username=${session.attributes.sub}&supplierTitle=${supplier}&perPage=1000&page=1`
  const data = await API.get('api', url)
  // API shape can be either top-level filteredWithVenueSuppliers or nested under venueReportArr
  return (
    data?.filteredWithVenueSuppliers ||
    data?.venueReportArr?.filteredWithVenueSuppliers ||
    []
  )
}

const SpendingCSV = ({ suppliers, startDate, endDate, tableRecords }) => {
  const [loading, setLoading] = useState(false)

  const onDownload = async () => {
    try {
      setLoading(true)
      // Fetch venue breakdowns for each supplier in parallel
      const uniqueSuppliers = Array.from(new Set(suppliers))
      const results = await Promise.all(
        uniqueSuppliers.map(s => fetchVenueBreakdown(startDate, endDate, s)),
      )
      const map = {}
      uniqueSuppliers.forEach((s, idx) => {
        map[s] = results[idx]
      })

      const rows = buildRows(tableRecords, map)

      // ✅ Prepend UTF-8 BOM so Excel reads correctly
      const csv = '\uFEFF' + buildCsvString(rows)

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        fileName(uniqueSuppliers, startDate, endDate),
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      onClick={onDownload}
      loading={loading}
      type={'primary'}
      style={{ width: 160, height: 40 }}
    >
      Download CSV
    </Button>
  )
}

export default SpendingCSV
